import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Lock } from "lucide-react";
import logoHorizontal from "@/assets/logo-horizontal.png";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setReady(true);
      }
    });

    // Also check if we already have a session (user clicked the link)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setReady(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: "As senhas não coincidem", variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: "A senha deve ter pelo menos 6 caracteres", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast({ title: "Erro ao atualizar senha", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Senha definida com sucesso!" });
      navigate("/dashboard");
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-jade-light flex items-center justify-center p-4 font-body">
        <div className="w-full max-w-md bg-card rounded-2xl shadow-xl border border-border p-8 text-center">
          <img src={logoHorizontal} alt="MentalSync" className="h-12 mx-auto mb-4" />
          <p className="text-muted-foreground">Verificando seu link...</p>
          <p className="text-sm text-muted-foreground mt-2">
            Se o link expirou, volte para o{" "}
            <button onClick={() => navigate("/auth")} className="text-gold hover:underline">
              login
            </button>{" "}
            e solicite um novo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jade-light flex items-center justify-center p-4 font-body">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-xl border border-border p-8">
        <div className="text-center mb-8">
          <img src={logoHorizontal} alt="MentalSync" className="h-12 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-foreground">Defina sua Senha</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Crie uma senha para acessar sua conta MentalSync
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-foreground">Nova senha</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="password"
                placeholder="Mínimo 6 caracteres"
                className="pl-10"
                value={password}
                onChange={e => setPassword(e.target.value)}
                minLength={6}
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-foreground">Confirmar senha</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="password"
                placeholder="Repita a senha"
                className="pl-10"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                minLength={6}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gold hover:bg-gold-dark text-accent-foreground font-semibold py-5"
          >
            {loading ? "Salvando..." : "Definir Senha e Entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
